/**
 * Display-currency store for the W12-F2-S3 normalization UX.
 *
 * Spec asked for Zustand `persist` — to avoid adding a new dep we use
 * a tiny React context + localStorage hand-roll instead. Same external
 * surface (a single hook returning `[currency, setCurrency]`), drop-in
 * upgrade to Zustand if/when we add the dep.
 *
 * The selected currency persists across reloads via localStorage.
 * Default is `EUR` so cross-building rollups have a sensible anchor
 * without prompting on first load. Users can pick any ISO 4217 code;
 * unknown codes degrade gracefully — the FX layer returns the line in
 * native currency when no rate is available.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'senviro.finance.displayCurrency';
const DEFAULT_CURRENCY = 'EUR';

interface CurrencyContextValue {
  currency: string;
  setCurrency: (next: string) => void;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(
  undefined,
);

export function CurrencyProvider({ children }: { children: ReactNode }): JSX.Element {
  const [currency, setCurrencyState] = useState<string>(() => {
    if (typeof window === 'undefined') return DEFAULT_CURRENCY;
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      return stored && stored.length === 3 ? stored : DEFAULT_CURRENCY;
    } catch {
      return DEFAULT_CURRENCY;
    }
  });

  const setCurrency = useCallback((next: string): void => {
    if (next.length !== 3) return;
    setCurrencyState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (SSR, sandboxed iframes) — fall back
      // to memory only; selection won't persist across reloads.
    }
  }, []);

  // Sync across tabs.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent): void {
      if (e.key === STORAGE_KEY && typeof e.newValue === 'string' && e.newValue.length === 3) {
        setCurrencyState(e.newValue);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({ currency, setCurrency }),
    [currency, setCurrency],
  );

  return (
    <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>
  );
}

export function useDisplayCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Sensible default outside the provider — useful for tests + the
    // BuildingFinancePage's transitional period before the provider
    // is mounted at the app root.
    return {
      currency: DEFAULT_CURRENCY,
      setCurrency: () => undefined,
    };
  }
  return ctx;
}

/** Whitelist of currencies the picker offers. UI-only — the API
 *  accepts any ISO 4217 the FX table happens to have a rate for. */
export const CURRENCY_OPTIONS: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'NOK', label: 'NOK — Norwegian krone' },
  { code: 'CHF', label: 'CHF — Swiss franc' },
  { code: 'USD', label: 'USD — US dollar' },
];
